import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api/client';
import { getPushState, type PushState } from '../lib/push';
import type { DashboardSummary, ScheduleSpec } from '../api/types';

const WD = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** Descreve o agendamento em linguagem natural (em vez do cron cru). */
function describeSchedule(spec: ScheduleSpec | null, cron: string | null): string {
  if (!spec) return cron ? `cron: ${cron}` : 'agendada';
  switch (spec.kind) {
    case 'interval':
      return spec.minutes % 60 === 0
        ? `a cada ${spec.minutes / 60} hora${spec.minutes / 60 > 1 ? 's' : ''}`
        : `a cada ${spec.minutes} minuto${spec.minutes > 1 ? 's' : ''}`;
    case 'daily':
      return `todo dia às ${spec.time}`;
    case 'weekly':
      return `toda ${WD[spec.weekday] ?? '?'} às ${spec.time}`;
    case 'monthly':
      return `todo dia ${spec.day} de cada mês às ${spec.time}`;
    case 'advanced':
      return cron ? `personalizado (${cron})` : 'personalizado';
  }
}

function formatNextRun(iso: string | null, trigger: string): string {
  if (trigger === 'email_received') return 'quando chegar e-mail';
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [push, setPush] = useState<PushState | null>(null);

  useEffect(() => { getPushState().then(setPush); }, []);

  const { data } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: async () => (await api.get<DashboardSummary>('/dashboard/summary')).data,
  });

  const notifAtivas = push === 'subscribed';

  return (
    <div className="space-y-4">
      <div className="card">
        <h2 className="text-xl font-bold">Olá, {user?.name || user?.email}</h2>
        <p className="text-sm text-slate-600 mt-1">Resumo do mês.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Faturamento do mês */}
        <Link to="/nfse" className="card hover:shadow-md transition">
          <div className="text-xs uppercase tracking-wide text-slate-400">
            Faturado em {data?.competencia ?? 'este mês'}
          </div>
          <div className="text-2xl font-bold text-emerald-600 mt-1">
            {data ? data.faturado_mes.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {data ? `${data.faturado_mes_qtd} nota${data.faturado_mes_qtd === 1 ? '' : 's'} autorizada${data.faturado_mes_qtd === 1 ? '' : 's'}` : ''}
          </div>
        </Link>

        {/* Notificações */}
        <Link to="/notificacoes" className="card hover:shadow-md transition">
          <div className="text-xs uppercase tracking-wide text-slate-400">Notificações neste dispositivo</div>
          <div className={`text-2xl font-bold mt-1 ${notifAtivas ? 'text-emerald-600' : 'text-slate-400'}`}>
            {push === null ? '—' : notifAtivas ? '● Ativas' : '○ Inativas'}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {notifAtivas ? 'Você recebe push quando as regras rodam/falham.' : 'Toque para ativar o push neste aparelho.'}
          </div>
        </Link>
      </div>

      {/* Regras ativas por próximo disparo */}
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Regras ativas</h3>
          <Link to="/regras" className="text-xs text-brand-600 hover:underline">gerenciar</Link>
        </div>
        {!data && <div className="text-sm text-slate-500">Carregando…</div>}
        {data && data.rules_ativas.length === 0 && (
          <div className="text-sm text-slate-500">Nenhuma regra ativa. <Link className="text-brand-600 hover:underline" to="/regras">Criar regra</Link>.</div>
        )}
        <ul className="divide-y">
          {data?.rules_ativas.map((r) => (
            <li key={r.id} className="py-2 flex items-center justify-between gap-2 text-sm">
              <div className="min-w-0">
                <div className="font-medium truncate">
                  {r.name}
                  {r.last_status === 'error' && <span className="text-xs text-red-600 ml-2">● último: erro</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {r.trigger_type === 'schedule' ? describeSchedule(r.schedule_spec, r.cron) : 'por e-mail recebido'}
                  {r.to_addr && <> · envia p/ {r.to_addr}</>}
                </div>
              </div>
              <div className="text-xs text-slate-600 shrink-0 text-right">
                <div className="text-slate-400">próximo</div>
                <div>{formatNextRun(r.next_run, r.trigger_type)}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="card">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold">Dispositivos</h3>
          <Link to="/registrar" className="text-xs text-brand-600 hover:underline">+ adicionar este dispositivo</Link>
        </div>
        <p className="text-sm text-slate-600">
          Você tem <strong>{user?.credentials_count ?? 0}</strong> dispositivo(s) registrado(s).
        </p>
        <p className="text-xs text-slate-400 mt-1">
          Pra usar no celular: se for o mesmo Apple ID/Google, a passkey sincroniza sozinha.
          Senão, no novo aparelho toque “Entrar com biometria” → “usar passkey de outro dispositivo” (QR),
          depois volte aqui e clique em adicionar.
        </p>
      </div>

      <div className="card">
        <h3 className="font-semibold">Atalhos</h3>
        <ul className="text-sm text-slate-600 list-disc pl-5 mt-2 space-y-1">
          <li><Link className="text-brand-600 hover:underline" to="/nfse">NFS-e</Link> — emissão padrão Nacional</li>
          <li><Link className="text-brand-600 hover:underline" to="/regras">Regras</Link> — envio agendado ou por e-mail recebido</li>
          <li><Link className="text-brand-600 hover:underline" to="/templates">Templates</Link> — DOCX/PDF com placeholders</li>
          <li><Link className="text-brand-600 hover:underline" to="/gmail">Contas Gmail</Link> · <Link className="text-brand-600 hover:underline" to="/notificacoes">Notificações</Link></li>
        </ul>
      </div>
    </div>
  );
}
